import { Selector, createSelector } from '@reduxjs/toolkit'
import { EntityType, CollectionStatus, CollectionType, Collection } from 'api'
import { UnassignedCollectionId, UnassignedCollectionName, CollectionHelper, compareCreatedAtDesc } from 'model'
import { GlobalState } from 'store'
import { GridId } from 'store/grid'
import { compareInsensitive } from 'utils'
import { createEntityGetterSelector, createAllEntitiesSelector } from './entitySelectors'
import { collectionStoreSelector } from './storeSelectors'
import { createSelectedCollectionIdSelector } from './selectionSelectors'
import { isOwnOrganisationGetterSelector } from './adminSelectors'
import { isAONXCollection, isNestedCollection, isSmartGroupCollection } from 'view/routes/requests/shared/utils'

export const availableCollectionsSelector: Selector<GlobalState, Collection[]> = createSelector(
  createAllEntitiesSelector(EntityType.COLLECTION),
  isOwnOrganisationGetterSelector,
  (allCollections, getIsOwnOrganisation) => {
    return allCollections
      .filter(
        collection =>
          getIsOwnOrganisation(collection.initiatorOrganisationId) &&
          collection.status === CollectionStatus.OPEN &&
          !isSmartGroupCollection(collection) &&
          !isAONXCollection(collection) &&
          !isNestedCollection(collection)
      )
      .sort((col1, col2) => compareInsensitive(CollectionHelper.getName(col1), CollectionHelper.getName(col2)))
  }
)

export const createAvailableCollectionIdsByNameAndTypeGetterSelector: Selector<
  GlobalState,
  (name: string, type: CollectionType) => string[]
> = createSelector(
  collectionStoreSelector,
  isOwnOrganisationGetterSelector,
  (collectionStore, getIsOwnOrganisation) => (name: string, type: CollectionType | null) =>
    Object.values(collectionStore)
      .filter(
        entry =>
          getIsOwnOrganisation(entry.lastUpdate.entity.initiatorOrganisationId) &&
          entry.lastUpdate.entity.status === CollectionStatus.OPEN &&
          entry.lastUpdate.entity.name.toUpperCase() === name.toUpperCase() &&
          (!type || entry.lastUpdate.entity.type === type)
      )
      .sort(compareCreatedAtDesc)
      .map(entry => entry.lastUpdate.entity.id)
)

export const createSelectedCollectionNameSelector = (gridId: GridId): Selector<GlobalState, string | null> =>
  createSelector(
    createSelectedCollectionIdSelector(gridId),
    createEntityGetterSelector(EntityType.COLLECTION),
    (collectionId, getCollection) => {
      if (collectionId === null) {
        return null
      }
      if (collectionId === UnassignedCollectionId) {
        return UnassignedCollectionName
      }
      const collection = getCollection(collectionId)
      return collection ? collection.name : null
    }
  )

export const stagingOrdersCollectionNameSelector = createSelectedCollectionNameSelector(GridId.StagingGrid)
export const initiatedNegotiationsCollectionNameSelector = createSelectedCollectionNameSelector(GridId.InitiatedGrid)
export const incomingNegotiationsCollectionNameSelector = createSelectedCollectionNameSelector(GridId.IncomingGrid)
export const completedNegotiationsCollectionNameSelector = createSelectedCollectionNameSelector(GridId.CompletedGrid)
